import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const shallowCloneObject = require('./shallowCloneObject');
const sameColumn = require('./ColumnComparer');
const ColumnUtils = require('./ColumnUtils');
const isColumnsImmutable = require('./utils/isColumnsImmutable');

export type Column = IEditableDataGridColumnDef & {
  left?: number;
  locked?: boolean;
  events?: any;
};

export type ColumnMetricsType = {
  columns: Array<Column>;
  totalWidth: number;
  minColumnWidth: number;
};

const setColumnWidths = (columns: Readonly<Array<Readonly<Column>>>, totalWidth: number): Array<Column> => {
  return columns.map((oldColumn) => {
    const column = { ...oldColumn };
    if (!!column.width) {
      if (/^([0-9]+)%$/.exec(column.width.toString())) {
        column.width = Math.floor((column.width / 100) * totalWidth);
      }
    }
    return column;
  });
};

const setDeferredColumnWidths = (
  columns: Readonly<Array<Readonly<Column>>>,
  unallocatedWidth: number,
  minColumnWidth: number,
  totalWidth: number
): Array<Column> => {
  const deferredColumns = columns.filter((c) => !c.width);

  if (deferredColumns.length === 0) {
    const currentTotalWidth = columns.reduce((w, column) => {
      return w + (column.width ? column.width : 0);
    }, 0);

    return columns.map((column) => {
      return { ...column, width: ((column.width ? column.width : minColumnWidth) / currentTotalWidth) * totalWidth };
    });
  }

  return columns.map((oldColumn) => {
    const column = { ...oldColumn };
    if (!column.width || column.width === 0) {
      if (unallocatedWidth <= 0) {
        column.width = minColumnWidth;
      } else {
        const baseColumnWidth = unallocatedWidth / ColumnUtils.getSize(deferredColumns, true);
        const columnWidth = Math.floor(baseColumnWidth * (column.widthFactor || 1));
        if (columnWidth < minColumnWidth) {
          column.width = minColumnWidth;
        } else {
          column.width = columnWidth;
        }
      }
    }
    return column;
  });
};

const setColumnOffsets = (columns: Readonly<Array<Readonly<Column>>>): Array<Column> => {
  let left = 0;
  return columns.map((oldColumn) => {
    const column = { ...oldColumn };
    column.left = left;
    if (column.width) {
      left += column.width;
    }
    return column;
  });
};

/**
 * Update column metrics calculation.
 *
 * @param columnMetricsType - ColumnMetricsType
 */
const recalculate = (columnMetricsType: ColumnMetricsType): ColumnMetricsType => {
  // compute width for columns which specify width
  let columns: Array<Column> = setColumnWidths(columnMetricsType.columns, columnMetricsType.totalWidth);
  const unallocatedWidth = columns
    .filter((c) => c.width)
    .reduce((w, column) => {
      return w - (column.width ? column.width : 0);
    }, columnMetricsType.totalWidth);

  const width = columns
    .filter((c) => c.width)
    .reduce((w, column) => {
      return w + (column.width ? column.width : 0);
    }, 0);

  // compute width for columns which doesn't specify width
  columns = setDeferredColumnWidths(
    columns,
    unallocatedWidth,
    columnMetricsType.minColumnWidth,
    columnMetricsType.totalWidth
  );

  // compute left offset
  columns = setColumnOffsets(columns);

  return {
    columns,
    width,
    totalWidth: columnMetricsType.totalWidth,
    minColumnWidth: columnMetricsType.minColumnWidth,
  } as ColumnMetricsType;
};

/**
 * Update column metrics calculation by resizing a column.
 *
 * @param metrics - the ColumnMetricsType metrics
 * @param index - column index
 * @param width - column width
 */
const resizeColumn = (metrics: ColumnMetricsType, index: number, width: number): ColumnMetricsType => {
  const column = ColumnUtils.getColumn(metrics.columns, index);
  let metricsClone = shallowCloneObject(metrics);
  metricsClone.columns = metrics.columns.slice(0);

  const updatedColumn = shallowCloneObject(column);
  updatedColumn.width = Math.max(width, metricsClone.minColumnWidth);

  metricsClone = ColumnUtils.spliceColumn(metricsClone, index, updatedColumn);

  return recalculate(metricsClone);
};

const areColumnsImmutable = (prevColumns: Readonly<Array<Column>>, nextColumns: Readonly<Array<Column>>): boolean => {
  return isColumnsImmutable(prevColumns) && isColumnsImmutable(nextColumns);
};

const compareEachColumn = (
  prevColumns: Readonly<Array<Column>>,
  nextColumns: Readonly<Array<Column>>,
  isSameColumn: (a: Column, b: Column) => boolean
): boolean => {
  let i;
  let len;
  let column;
  const prevColumnsByKey: { [key: string]: Column } = {};
  const nextColumnsByKey: { [key: string]: Column } = {};

  if (ColumnUtils.getSize(prevColumns) !== ColumnUtils.getSize(nextColumns)) {
    return false;
  }

  for (i = 0, len = ColumnUtils.getSize(prevColumns); i < len; i++) {
    column = prevColumns[i];
    prevColumnsByKey[column.key] = column;
  }

  for (i = 0, len = ColumnUtils.getSize(nextColumns); i < len; i++) {
    column = nextColumns[i];
    nextColumnsByKey[column.key] = column;
    const prevColumn = prevColumnsByKey[column.key];
    if (prevColumn === undefined || !isSameColumn(prevColumn, column)) {
      return false;
    }
  }

  for (i = 0, len = ColumnUtils.getSize(prevColumns); i < len; i++) {
    column = prevColumns[i];
    const nextColumn = nextColumnsByKey[column.key];
    if (nextColumn === undefined) {
      return false;
    }
  }
  return true;
};

const sameColumns = (
  prevColumns: Readonly<Array<Column>>,
  nextColumns: Readonly<Array<Column>>,
  isSameColumn: (a: Column, b: Column) => boolean
): boolean => {
  if (areColumnsImmutable(prevColumns, nextColumns)) {
    return prevColumns === nextColumns;
  }

  return compareEachColumn(prevColumns, nextColumns, isSameColumn);
};

module.exports = { recalculate, resizeColumn, sameColumn, sameColumns };
